import type { AuthTokens, User } from '@/shared/types/auth.types'

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function isValidToken(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim() !== '' &&
    value !== 'undefined' &&
    value !== 'null'
  )
}

export function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((char) => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )

    const parsed: unknown = JSON.parse(jsonPayload)
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export function getUserFromTokens(tokens: AuthTokens | null): User | null {
  const tokenToDecode = tokens?.idToken ?? tokens?.accessToken
  if (!tokenToDecode) {
    return null
  }

  const decoded = decodeToken(tokenToDecode)
  if (!decoded) {
    return null
  }

  const sub = asString(decoded.sub)

  return {
    phoneNumber:
      asString(decoded.phone_number) || asString(decoded['custom:phone_number']) || '',
    name: asString(decoded.name) || asString(decoded.given_name) || '',
    role: (() => {
      const role = asString(decoded['custom:role'])
      return role === 'buyer' || role === 'vendor' || role === 'admin' ? role : 'buyer'
    })(),
    email: asString(decoded.email) || undefined,
    cognitoSub: sub || undefined,
  }
}

export function isWhatsAppUser(tokens: AuthTokens | null): boolean {
  const tokenToDecode = tokens?.idToken ?? tokens?.accessToken
  if (!tokenToDecode) {
    return false
  }

  const decoded = decodeToken(tokenToDecode)
  if (!decoded) {
    return false
  }

  const identities = decoded.identities
  if (Array.isArray(identities)) {
    const provider = (identities[0] as { providerName?: unknown } | undefined)?.providerName
    if (provider === 'Google') {
      return false
    }
  }

  const phoneNumber =
    asString(decoded.phone_number) || asString(decoded['custom:phone_number'])
  return phoneNumber.trim() !== ''
}

export function getTokenExpirationTime(tokens: AuthTokens | null): number | null {
  if (!tokens?.accessToken) {
    return null
  }

  const decoded = decodeToken(tokens.accessToken)
  const exp = asNumber(decoded?.exp)
  return exp ? exp * 1000 : null
}

export function isTokenExpired(tokens: AuthTokens | null, bufferMs = 0): boolean {
  const expirationTime = getTokenExpirationTime(tokens)
  if (!expirationTime) {
    return true
  }

  return Date.now() + bufferMs >= expirationTime
}

export function getTimeUntilExpiration(tokens: AuthTokens | null): number | null {
  const expirationTime = getTokenExpirationTime(tokens)
  if (!expirationTime) {
    return null
  }

  return Math.max(0, expirationTime - Date.now())
}
